import React, { useState, lazy, Component } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BuildingIcon, UsersIcon, SparklesIcon, BellIcon, CreditCardIcon, ShieldAlertIcon, CheckCircle2Icon, PlusIcon, TrashIcon, ShieldIcon, UploadIcon, ExternalLinkIcon, MapPinIcon, ChevronDownIcon, CheckIcon, XIcon, BotIcon, FileTextIcon, GlobeIcon, MailIcon, ShieldCheckIcon } from 'lucide-react';
import { useBusiness, BUSINESS_TYPE_LABEL, BusinessType } from './BusinessContext';
import { InviteMemberModal, TeamMember, ROLE_PRESETS, accessSummary } from './InviteMemberModal';
import { UpgradePlanModal } from './UpgradePlanModal';
import { useAuth } from './AuthContext';
const INITIAL_MEMBERS: TeamMember[] = [{
  name: 'Fadi',
  role: 'Admin',
  email: 'fadi@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Admin')!.access,
  canViewContact: true
}, {
  name: 'Sara',
  role: 'Sales',
  email: 'sara@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Sales')!.access,
  canViewContact: true
}, {
  name: 'Sales Team',
  role: 'Viewer',
  email: 'team@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Viewer')!.access,
  canViewContact: false
}];
type Section = 'Business Profile' | 'Team Members' | 'AI Channels' | 'Notifications' | 'Plan & Billing' | 'Danger Zone';
function slugify(value: string) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
export function BusinessSettings() {
  const {
    hub,
    currentPlanId,
    listingCount,
    PACKAGES,
    updateHubProfile,
    subscribed,
    cancelSubscription
  } = useBusiness();
  const {
    idVerified,
    markIdVerified
  } = useAuth();
  const [section, setSection] = useState<Section>('Business Profile');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [shopSlug, setShopSlug] = useState(hub?.shopSlug || '');
  const [logo, setLogo] = useState(hub?.logo || '');
  const [location, setLocation] = useState('Dubai, UAE');
  const [mapPin, setMapPin] = useState('');
  const [members, setMembers] = useState<TeamMember[]>(INITIAL_MEMBERS);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeInitialPlan, setUpgradeInitialPlan] = useState<string | undefined>();
  const [accountType, setAccountType] = useState<'personal' | 'business'>('personal');
  const [tradeLicenseUploaded, setTradeLicenseUploaded] = useState(false);
  const [website, setWebsite] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const paidPackages = PACKAGES.filter(p => p.id !== 'Free');
  const openUpgrade = (planId?: string) => {
    setUpgradeInitialPlan(planId);
    setUpgradeOpen(true);
  };
  const openInvite = () => {
    setEditingMember(null);
    setInviteOpen(true);
  };
  const openEdit = (m: TeamMember) => {
    setEditingMember(m);
    setInviteOpen(true);
  };
  const saveMember = (m: TeamMember) => {
    setMembers(prev => {
      const idx = prev.findIndex(x => x.email === m.email && x.email !== '');
      if (idx >= 0) return prev.map((x, i) => i === idx ? m : x);
      return [...prev, m];
    });
    setInviteOpen(false);
  };
  const removeMember = (email: string) => setMembers(prev => prev.filter(x => x.email !== email));
  const sections: {
    id: Section;
    icon: React.ReactNode;
  }[] = [{
    id: 'Business Profile',
    icon: <BuildingIcon className="w-4 h-4" data-id="element-750" />
  }, {
    id: 'Team Members',
    icon: <UsersIcon className="w-4 h-4" data-id="element-751" />
  }, {
    id: 'AI Channels',
    icon: <BotIcon className="w-4 h-4" data-id="element-752" />
  }, {
    id: 'Notifications',
    icon: <BellIcon className="w-4 h-4" data-id="element-753" />
  }, {
    id: 'Plan & Billing',
    icon: <CreditCardIcon className="w-4 h-4" data-id="element-754" />
  }, {
    id: 'Danger Zone',
    icon: <ShieldAlertIcon className="w-4 h-4" data-id="element-755" />
  }];
  return <div className="w-full h-full flex flex-col pb-24" data-id="element-756">
      <div className="flex flex-col gap-4 pt-2" data-id="element-757">
        {/* Section nav */}
        <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 hide-scrollbar" data-id="element-758">
          {sections.map(s => <button key={s.id} onClick={() => setSection(s.id)} className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors border ${section === s.id ? 'bg-primary-indigo text-white border-primary-indigo shadow-sm' : 'bg-white text-slate-600 border-slate-200'} ${s.id === 'Danger Zone' && section !== s.id ? 'text-red-600 border-red-100' : ''}`} data-id="element-759">
              <span className={section === s.id ? 'text-white' : s.id === 'Danger Zone' ? 'text-red-500' : 'text-slate-400'} data-id="element-760">
                {s.icon}
              </span>
              {s.id}
            </button>)}
        </div>

        {/* Content */}
        <motion.div key={section} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex-1 space-y-4" data-id="element-761">
          {section === 'Business Profile' && <Card data-id="element-762">
              <SectionTitle title="Business Profile" desc="This information appears across TALAB." data-id="element-763" />
              <div className="space-y-5" data-id="element-764">
                {/* Account Verification Status */}
                {idVerified ? <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-2xl" data-id="element-765">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0" data-id="element-766">
                      <ShieldCheckIcon className="w-5 h-5 text-emerald-600" data-id="element-767" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-768">
                      <p className="text-[13px] font-bold text-emerald-900 leading-tight" data-id="element-769">
                        Account verified
                      </p>
                      <p className="text-[11px] text-emerald-700/80 leading-tight mt-0.5" data-id="element-770">
                        Your identity has been confirmed.
                      </p>
                    </div>
                    <CheckCircle2Icon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-771" />
                  </div> : <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-id="element-772">
                    <div className="flex items-center gap-3 mb-3" data-id="element-773">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0" data-id="element-774">
                        <ShieldCheckIcon className="w-5 h-5 text-amber-600" data-id="element-775" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-776">
                        <p className="text-[13px] font-bold text-amber-900 leading-tight" data-id="element-777">
                          Account not verified
                        </p>
                        <p className="text-[11px] text-amber-700/90 leading-tight mt-0.5" data-id="element-778">
                          Verify your identity by uploading or scanning your
                          Emirates ID.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2" data-id="element-779">
                      <div className="relative flex-1" data-id="element-780">
                        <input type="file" accept=".pdf,image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={e => {
                    if (e.target.files && e.target.files.length > 0) {
                      markIdVerified();
                    }
                  }} data-id="element-781" />
                        <div className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white border border-amber-200 rounded-xl text-[12.5px] font-bold text-amber-800" data-id="element-782">
                          <UploadIcon className="w-4 h-4" data-id="element-783" /> Upload ID
                        </div>
                      </div>
                      <button onClick={markIdVerified} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-500 text-white rounded-xl text-[12.5px] font-bold active:scale-[0.98] transition-transform" data-id="element-784">
                        <ShieldCheckIcon className="w-4 h-4" data-id="element-785" /> Scan ID
                      </button>
                    </div>
                  </div>}

                {/* Account Type Chooser */}
                <div className="flex flex-col gap-3" data-id="element-786">
                  <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider ml-1" data-id="element-787">
                    Account Type
                  </label>
                  <div className="flex bg-slate-100 p-1 rounded-2xl" data-id="element-788">
                    <button onClick={() => setAccountType('personal')} className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${accountType === 'personal' ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 active:text-slate-700'}`} data-id="element-789">
                      Personal Seller
                    </button>
                    <button onClick={() => setAccountType('business')} className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${accountType === 'business' ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 active:text-slate-700'}`} data-id="element-790">
                      Business
                    </button>
                  </div>
                </div>

                {/* Business Verification Block */}
                {accountType === 'business' && <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-4" data-id="element-791">
                    <div className="flex items-center gap-2 mb-2" data-id="element-792">
                      <ShieldCheckIcon className="w-5 h-5 text-primary-indigo" data-id="element-793" />
                      <h3 className="text-sm font-bold text-slate-800" data-id="element-794">
                        Business Verification
                      </h3>
                    </div>

                    {/* Trade License */}
                    <div data-id="element-795">
                      <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 ml-1" data-id="element-796">
                        Trade License
                      </label>
                      <div className="relative" data-id="element-797">
                        <input type="file" accept=".pdf,image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={e => {
                    if (e.target.files && e.target.files.length > 0) {
                      setTradeLicenseUploaded(true);
                    }
                  }} data-id="element-798" />
                        <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${tradeLicenseUploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`} data-id="element-799">
                          <div className="flex items-center gap-2.5" data-id="element-800">
                            <FileTextIcon className={`w-4 h-4 ${tradeLicenseUploaded ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-801" />
                            <span className={`text-[13px] font-medium ${tradeLicenseUploaded ? 'text-emerald-700' : 'text-slate-600'}`} data-id="element-802">
                              {tradeLicenseUploaded ? 'License Uploaded' : 'Upload Trade License'}
                            </span>
                          </div>
                          {tradeLicenseUploaded && <CheckCircle2Icon className="w-4 h-4 text-emerald-600" data-id="element-803" />}
                        </div>
                      </div>
                    </div>

                    {/* Website */}
                    <div data-id="element-804">
                      <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 ml-1" data-id="element-805">
                        Company Website
                      </label>
                      <div className="relative" data-id="element-806">
                        <GlobeIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-807" />
                        <input type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://yourcompany.com" className="w-full pl-9 pr-3.5 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-[13px] outline-none" data-id="element-808" />
                      </div>
                    </div>

                    {/* Email Verification */}
                    <div data-id="element-809">
                      <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5 ml-1" data-id="element-810">
                        Business Email
                      </label>
                      <div className="flex gap-2" data-id="element-811">
                        <div className="relative flex-1" data-id="element-812">
                          <MailIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-813" />
                          <input type="email" value={businessEmail} onChange={e => {
                      setBusinessEmail(e.target.value);
                      setEmailVerified(false);
                    }} placeholder="you@company.com" className={`w-full pl-9 pr-3.5 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-[13px] outline-none ${emailVerified ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`} data-id="element-814" />
                        </div>
                        <button onClick={() => {
                    if (businessEmail) setEmailVerified(true);
                  }} disabled={!businessEmail || emailVerified} className={`px-4 py-2.5 rounded-xl text-[13px] font-bold transition-colors flex-shrink-0 ${emailVerified ? 'bg-emerald-100 text-emerald-700' : businessEmail ? 'bg-slate-800 text-white active:scale-95' : 'bg-slate-100 text-slate-400'}`} data-id="element-815">
                          {emailVerified ? 'Verified' : 'Verify'}
                        </button>
                      </div>
                    </div>
                  </div>}

                <div className="flex flex-col gap-4" data-id="element-816">
                  <div className="flex items-center gap-4" data-id="element-817">
                    <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden group cursor-pointer flex-shrink-0" data-id="element-818">
                      {logo ? <img src={logo} alt="Shop Logo" className="w-full h-full object-cover" data-id="element-819" /> : <>
                          <UploadIcon className="w-5 h-5 text-slate-400 mb-1" data-id="element-820" />
                          <span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider" data-id="element-821">
                            {accountType === 'personal' ? 'Photo' : 'Logo'}
                          </span>
                        </>}
                      <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={e => {
                    if (e.target.files && e.target.files[0]) {
                      const url = URL.createObjectURL(e.target.files[0]);
                      setLogo(url);
                    }
                  }} data-id="element-822" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-823">
                      <Field key={accountType} label={accountType === 'personal' ? 'Full Name' : 'Business Name'} defaultValue={hub?.name ?? (accountType === 'personal' ? 'Your Name' : 'Your Business')} onChange={val => {
                    updateHubProfile({
                      name: val
                    });
                    if (accountType === 'personal') {
                      setShopSlug(slugify(val));
                    }
                  }} data-id="element-824" />
                    </div>
                  </div>
                  <div data-id="element-825">
                    <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-826">
                      {accountType === 'personal' ? 'Your Profile Link' : 'Shop Handle'}
                    </label>
                    <div className="flex items-center" data-id="element-827">
                      <span className="px-3 py-2.5 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-slate-500 text-[13px] font-medium" data-id="element-828">
                        talab.app/
                      </span>
                      <input value={shopSlug} readOnly={accountType === 'personal'} onChange={e => setShopSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder={accountType === 'personal' ? 'auto-generated' : 'shopname'} className={`flex-1 px-3 py-2.5 border border-slate-200 rounded-r-xl text-[13px] outline-none ${accountType === 'personal' ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-slate-50 focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo'}`} data-id="element-829" />
                    </div>
                    {accountType === 'personal' && <p className="mt-1.5 text-[11px] text-slate-400 ml-1" data-id="element-830">
                        Your link is created automatically from your name.
                      </p>}
                    {shopSlug && <div className="mt-1.5 flex items-center gap-1 text-[11px] text-primary-indigo font-medium ml-1" data-id="element-831">
                        <ExternalLinkIcon className="w-3 h-3" data-id="element-832" />
                        <a href={`/shop/${shopSlug}`} target="_blank" rel="noreferrer" data-id="element-833">
                          talab.app/{shopSlug}
                        </a>
                      </div>}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4" data-id="element-834">
                  {accountType === 'business' && <SelectField label="Business Type" value={BUSINESS_TYPE_LABEL[hub?.type ?? 'generic']} options={Object.values(BUSINESS_TYPE_LABEL)} onChange={val => {
                const entry = (Object.entries(BUSINESS_TYPE_LABEL) as [BusinessType, string][]).find(([, label]) => label === val);
                if (entry) updateHubProfile({
                  type: entry[0]
                });
              }} data-id="element-835" />}
                  <div data-id="element-836">
                    <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-837">
                      Location Name
                    </label>
                    <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Al Quoz, Dubai, UAE" className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-[13px] outline-none" data-id="element-838" />
                  </div>
                </div>

                {/* Map pin */}
                <div data-id="element-839">
                  <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-840">
                    Pin Location on Maps
                  </label>
                  <div className="flex flex-col gap-2" data-id="element-841">
                    <div className="relative" data-id="element-842">
                      <MapPinIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-843" />
                      <input value={mapPin} onChange={e => setMapPin(e.target.value)} placeholder="Paste link or coordinates" className="w-full pl-9 pr-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-[13px] outline-none" data-id="element-844" />
                    </div>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapPin || location || 'Dubai, UAE')}`} target="_blank" rel="noreferrer" className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-[13px] font-medium rounded-xl active:bg-slate-50 flex items-center justify-center gap-1.5" data-id="element-845">
                      <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-846" /> Open Maps
                    </a>
                  </div>

                  {/* Map preview */}
                  <div className="mt-3 rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-id="element-847">
                    <iframe title="Business location preview" className="w-full h-40" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src={`https://www.google.com/maps?q=${encodeURIComponent(mapPin || location || 'Dubai, UAE')}&output=embed`} data-id="element-848" />
                  </div>
                </div>
                <Field label="Contact Email" defaultValue="business@email.com" data-id="element-849" />
                <Field label="Phone" defaultValue="+971 4 000 0000" data-id="element-850" />
                <TextAreaField label="About" defaultValue="Premium vehicle rentals across the UAE." data-id="element-851" />
              </div>
              <SaveBar onSave={() => updateHubProfile({
            logo,
            shopSlug
          })} data-id="element-852" />
            </Card>}

          {section === 'Team Members' && <Card data-id="element-853">
              <div className="flex items-center justify-between mb-4" data-id="element-854">
                <SectionTitle title="Team Members" desc="Manage who can access this hub." noMargin data-id="element-855" />
                <button onClick={openInvite} className="w-8 h-8 bg-primary-indigo text-white rounded-full active:scale-95 flex items-center justify-center shadow-sm" data-id="element-856">
                  <PlusIcon className="w-4 h-4" data-id="element-857" />
                </button>
              </div>
              <div className="space-y-2.5" data-id="element-858">
                {members.map(m => <div key={m.email || m.name} className="flex flex-col bg-slate-50 border border-slate-100 rounded-xl p-3" data-id="element-859">
                    <div className="flex items-center gap-3 mb-3" data-id="element-860">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple text-white font-bold flex items-center justify-center text-sm flex-shrink-0" data-id="element-861">
                        {m.name.charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-862">
                        <div className="font-medium text-dark-navy text-[13px] truncate" data-id="element-863">
                          {m.name}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate" data-id="element-864">
                          {m.email}
                        </div>
                      </div>
                      <span className="text-[10px] font-bold text-slate-600 px-2 py-0.5 bg-white border border-slate-200 rounded-md flex-shrink-0" data-id="element-865">
                        {m.role}
                      </span>
                    </div>
                    {confirmRemove === (m.email || m.name) ? <div className="flex flex-col gap-2 pt-3 border-t border-slate-200/60" data-id="element-866">
                        <p className="text-[12px] text-slate-600" data-id="element-867">
                          Remove{' '}
                          <span className="font-semibold text-dark-navy" data-id="element-868">
                            {m.name}
                          </span>{' '}
                          from this hub? They'll lose all access.
                        </p>
                        <div className="flex gap-2" data-id="element-869">
                          <button onClick={() => setConfirmRemove(null)} className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-700 text-[13px] font-bold rounded-xl active:bg-slate-50" data-id="element-870">
                            Cancel
                          </button>
                          <button onClick={() => {
                    removeMember(m.email);
                    setConfirmRemove(null);
                  }} className="flex-1 py-2.5 bg-red-600 text-white text-[13px] font-bold rounded-xl active:bg-red-700 flex items-center justify-center gap-1.5" data-id="element-871">
                            <TrashIcon className="w-3.5 h-3.5" data-id="element-872" /> Remove
                          </button>
                        </div>
                      </div> : <div className="flex items-center gap-2 pt-3 border-t border-slate-200/60" data-id="element-873">
                        <button onClick={() => openEdit(m)} className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-700 active:bg-slate-50 rounded-xl text-[13px] font-bold flex items-center justify-center gap-1.5" data-id="element-874">
                          <ShieldIcon className="w-4 h-4 text-slate-400" data-id="element-875" /> Edit
                          access
                        </button>
                        {m.role !== 'Admin' && <button onClick={() => setConfirmRemove(m.email || m.name)} aria-label={`Remove ${m.name}`} className="w-11 flex-shrink-0 py-2.5 bg-white border border-red-200 text-red-500 active:bg-red-50 rounded-xl flex items-center justify-center" data-id="element-876">
                            <TrashIcon className="w-4 h-4" data-id="element-877" />
                          </button>}
                      </div>}
                  </div>)}
              </div>
            </Card>}

          {section === 'AI Channels' && <Card data-id="element-878">
              <SectionTitle title="AI Channels" desc="Control how Talab represents your business." data-id="element-879" />
              <div className="space-y-1" data-id="element-880">
                <Toggle label="Auto-respond to questions" desc="Talab answers using your AI Knowledge rules." on data-id="element-881" />
                <Toggle label="Qualify leads automatically" desc="Talab asks budget and timeline before creating a lead." on data-id="element-882" />
                <Toggle label="Suggest upgrades & add-ons" desc="Offer delivery, insurance, and extras when relevant." on data-id="element-883" />
                <Toggle label="Notify me before sharing contact" desc="Ask for approval before exposing seller contact." data-id="element-884" />
              </div>
            </Card>}

          {section === 'Notifications' && <Card data-id="element-885">
              <SectionTitle title="Notifications" desc="Choose what you want to be alerted about." data-id="element-886" />
              <div className="space-y-1" data-id="element-887">
                <Toggle label="New leads" desc="When Talab qualifies a new buyer." on data-id="element-888" />
                <Toggle label="Hot leads" desc="High-intent buyers ready to purchase." on data-id="element-889" />
                <Toggle label="New messages" desc="Customer replies in Conversations." on data-id="element-890" />
                <Toggle label="Weekly demand report" desc="Search and interest signal summary." data-id="element-891" />
              </div>
            </Card>}

          {section === 'Plan & Billing' && <Card data-id="element-892">
              <SectionTitle title="Plan & Billing" desc="Manage your subscription and payment method." data-id="element-893" />
              <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-5 text-white mb-5" data-id="element-894">
                <div className="flex items-center justify-between mb-4" data-id="element-895">
                  <div data-id="element-896">
                    <div className="text-[11px] text-indigo-200 uppercase tracking-wider mb-0.5" data-id="element-897">
                      Current Plan
                    </div>
                    <div className="text-xl font-display font-bold" data-id="element-898">
                      {currentPlan.name}
                    </div>
                  </div>
                  <span className="px-2.5 py-1 bg-white/20 rounded-full text-[10px] font-bold" data-id="element-899">
                    Active
                  </span>
                </div>

                <div className="mb-4" data-id="element-900">
                  <div className="flex items-center justify-between text-[11px] mb-1.5" data-id="element-901">
                    <span className="text-indigo-100" data-id="element-902">Listing Usage</span>
                    <span className="font-medium" data-id="element-903">
                      {listingCount} of{' '}
                      {currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit}{' '}
                      listings
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-black/20 rounded-full overflow-hidden" data-id="element-904">
                    <div className="h-full bg-white/80 rounded-full transition-all" style={{
                  width: `${Math.min(100, listingCount / (currentPlan.listingLimit === Infinity ? Math.max(listingCount, 1) : currentPlan.listingLimit) * 100)}%`
                }} data-id="element-905" />
                  </div>
                </div>

                <div className="flex flex-col gap-2 text-[11px] text-indigo-100" data-id="element-906">
                  <span data-id="element-907">
                    {currentPlan.price === 0 ? 'Free forever' : `AED ${currentPlan.price.toLocaleString()} / month · Renews soon`}
                  </span>
                  {currentPlan.price > 0 && <button className="text-white font-medium underline underline-offset-2 self-start" data-id="element-908">
                      Update Payment Method
                    </button>}
                </div>
              </div>

              <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 px-4 py-4" data-id="element-909">
                <div data-id="element-910">
                  <div className="font-bold text-sm text-dark-navy mb-0.5" data-id="element-911">
                    Need more listings?
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug" data-id="element-912">
                    Compare plans and unlock a higher listing capacity.
                  </p>
                </div>
                <button onClick={() => openUpgrade()} className="w-full py-2.5 bg-primary-indigo text-white text-[13px] font-bold rounded-xl active:scale-95 transition-transform" data-id="element-913">
                  {currentPlan.price > 0 ? 'Change Plan' : 'Upgrade Plan'}
                </button>
              </div>

              {(subscribed || currentPlan.price > 0) && <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-914">
                  {!cancelOpen ? <div className="flex flex-col gap-3" data-id="element-915">
                      <div data-id="element-916">
                        <div className="font-medium text-dark-navy text-[13px] mb-0.5" data-id="element-917">
                          Cancel subscription
                        </div>
                        <p className="text-[11px] text-slate-500 leading-snug" data-id="element-918">
                          You'll keep {currentPlan.name} access until the end of
                          your billing period, then move to the Free plan.
                        </p>
                      </div>
                      <button onClick={() => setCancelOpen(true)} className="w-full py-2.5 bg-white border border-red-200 text-red-600 text-[13px] font-medium rounded-xl active:bg-red-50 transition-colors" data-id="element-919">
                        Cancel subscription
                      </button>
                    </div> : <div className="rounded-2xl border border-red-100 bg-red-50 p-4" data-id="element-920">
                      <div className="font-bold text-red-900 text-[13px] mb-1" data-id="element-921">
                        Cancel your {currentPlan.name} plan?
                      </div>
                      <p className="text-[11px] text-red-700/80 mb-4 leading-snug" data-id="element-922">
                        Your listing limit will drop to the Free plan (1
                        listing). Listings beyond the free limit will be paused.
                      </p>

                      <div className="mb-4" data-id="element-923">
                        <label className="block text-[11px] font-medium text-red-900 mb-2" data-id="element-924">
                          Please tell us why you're leaving
                          <span className="text-red-500" data-id="element-925"> *</span>
                        </label>
                        <div className="space-y-2" data-id="element-926">
                          {['Too expensive', "I'm not using it enough", 'Missing features I need', 'Switching to another tool', 'Just a temporary pause', 'Other'].map(reason => <label key={reason} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white cursor-pointer transition-colors text-[13px] ${cancelReason === reason ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200'}`} data-id="element-927">
                              <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={e => setCancelReason(e.target.value)} className="accent-red-600" data-id="element-928" />
                              <span className="text-slate-700" data-id="element-929">{reason}</span>
                            </label>)}
                        </div>
                        {cancelReason === 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Tell us more…" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-[13px] outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-930" />}
                        {cancelReason && cancelReason !== 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Anything else you'd like to add? (optional)" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-[13px] outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-931" />}
                      </div>

                      <div className="flex flex-col gap-2" data-id="element-932">
                        <button disabled={!cancelReason || cancelReason === 'Other' && !cancelNote.trim()} onClick={() => {
                  cancelSubscription();
                  setCancelOpen(false);
                  setCancelReason('');
                  setCancelNote('');
                }} className="w-full py-2.5 bg-red-600 text-white text-[13px] font-bold rounded-xl active:bg-red-700 transition-colors disabled:opacity-40" data-id="element-933">
                          Submit & cancel
                        </button>
                        <button onClick={() => {
                  setCancelOpen(false);
                  setCancelReason('');
                  setCancelNote('');
                }} className="w-full py-2.5 text-slate-600 text-[13px] font-medium active:bg-white rounded-xl" data-id="element-934">
                          Keep my plan
                        </button>
                      </div>
                    </div>}
                </div>}
            </Card>}

          {section === 'Danger Zone' && <div className="bg-red-50 border border-red-100 rounded-2xl p-5 space-y-4" data-id="element-935">
              <SectionTitle title="Danger Zone" desc="These actions are permanent." noMargin danger data-id="element-936" />
              <div className="flex flex-col gap-3 pt-2" data-id="element-937">
                <div data-id="element-938">
                  <div className="font-medium text-red-900 text-[13px]" data-id="element-939">
                    Deactivate Business Hub
                  </div>
                  <div className="text-[11px] text-red-700/80" data-id="element-940">
                    Temporarily hide your hub and listings.
                  </div>
                </div>
                <button className="w-full py-2.5 bg-white border border-red-200 text-red-700 text-[13px] font-medium rounded-xl active:bg-red-50" data-id="element-941">
                  Deactivate
                </button>
              </div>
              <div className="h-px bg-red-100" data-id="element-942" />
              <div className="flex flex-col gap-3" data-id="element-943">
                <div data-id="element-944">
                  <div className="font-medium text-red-900 text-[13px]" data-id="element-945">
                    Delete Business Hub
                  </div>
                  <div className="text-[11px] text-red-700/80" data-id="element-946">
                    Permanently remove your hub, catalog, and leads.
                  </div>
                </div>
                <button className="w-full py-2.5 bg-red-600 text-white text-[13px] font-medium rounded-xl active:bg-red-700 flex items-center justify-center gap-1.5" data-id="element-947">
                  <TrashIcon className="w-4 h-4" data-id="element-948" /> Delete
                </button>
              </div>
            </div>}
        </motion.div>
      </div>

      {inviteOpen && <InviteMemberModal member={editingMember} onClose={() => setInviteOpen(false)} onSave={saveMember} data-id="element-949" />}

      {upgradeOpen && <UpgradePlanModal initialPlan={upgradeInitialPlan} onClose={() => setUpgradeOpen(false)} data-id="element-950" />}
    </div>;
}
function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="bg-white border border-slate-200/70 rounded-3xl p-5 shadow-[0_1px_3px_rgb(0,0,0,0.03)]" data-id="element-951">
      {children}
    </div>;
}
function SectionTitle({
  title,
  desc,
  noMargin,
  danger
}: {
  title: string;
  desc: string;
  noMargin?: boolean;
  danger?: boolean;
}) {
  return <div className={noMargin ? '' : 'mb-5'} data-id="element-952">
      <h3 className={`text-[11px] font-bold uppercase tracking-wider ${danger ? 'text-red-500' : 'text-slate-400'}`} data-id="element-953">
        {title}
      </h3>
      <p className={`text-[13px] mt-1 leading-snug ${danger ? 'text-red-700/80' : 'text-slate-500'}`} data-id="element-954">
        {desc}
      </p>
    </div>;
}
function Field({
  label,
  defaultValue,
  onChange
}: {
  label: string;
  defaultValue?: string;
  onChange?: (val: string) => void;
}) {
  return <div data-id="element-955">
      <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-956">
        {label}
      </label>
      <input defaultValue={defaultValue} onChange={e => onChange?.(e.target.value)} className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-[14px] outline-none transition-all" data-id="element-957" />
    </div>;
}
function TextAreaField({
  label,
  defaultValue
}: {
  label: string;
  defaultValue?: string;
}) {
  return <div data-id="element-958">
      <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-959">
        {label}
      </label>
      <textarea rows={3} defaultValue={defaultValue} className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-[14px] outline-none transition-all resize-none" data-id="element-960" />
    </div>;
}
function SelectField({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange?: (val: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return <div data-id="element-961">
      <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-962">
        {label}
      </label>
      <button onClick={() => setOpen(true)} className="w-full flex items-center justify-between px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-medium text-slate-700 text-left active:bg-slate-100 transition-colors" data-id="element-963">
        <span className="truncate" data-id="element-964">{value}</span>
        <ChevronDownIcon className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-965" />
      </button>
      <OptionSheet open={open} title={label} options={options} active={value} onSelect={v => {
      onChange?.(v);
      setOpen(false);
    }} onClose={() => setOpen(false)} data-id="element-966" />
    </div>;
}
function SaveBar({
  onSave
}: {
  onSave?: () => void;
}) {
  return <div className="flex flex-col-reverse sm:flex-row gap-3 pt-5 mt-5 border-t border-slate-100" data-id="element-967">
      <button className="w-full py-3.5 text-slate-600 text-[14px] font-bold active:bg-slate-50 rounded-2xl transition-colors" data-id="element-968">
        Cancel
      </button>
      <button onClick={onSave} className="w-full py-3.5 bg-primary-indigo text-white text-[14px] font-bold rounded-2xl active:scale-[0.98] transition-transform shadow-sm flex items-center justify-center gap-2" data-id="element-969">
        <CheckCircle2Icon className="w-4 h-4" data-id="element-970" /> Save Changes
      </button>
    </div>;
}
function Toggle({
  label,
  desc,
  on
}: {
  label: string;
  desc: string;
  on?: boolean;
}) {
  const [enabled, setEnabled] = useState(!!on);
  return <div className="flex items-center justify-between py-3.5 border-b border-slate-50 last:border-0" data-id="element-971">
      <div className="pr-4" data-id="element-972">
        <div className="font-semibold text-dark-navy text-[14px] mb-0.5" data-id="element-973">
          {label}
        </div>
        <div className="text-[12px] text-slate-500 leading-snug" data-id="element-974">{desc}</div>
      </div>
      <button onClick={() => setEnabled(e => !e)} role="switch" aria-checked={enabled} aria-label={label} className={`relative w-12 h-7 rounded-full flex-shrink-0 transition-colors ${enabled ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-975">
        <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} data-id="element-976" />
      </button>
    </div>;
}
function OptionSheet({
  open,
  title,
  options,
  active,
  onSelect,
  onClose
}: {
  open: boolean;
  title: string;
  options: string[];
  active: string;
  onSelect: (v: string) => void;
  onClose: () => void;
}) {
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-977">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-[110] flex flex-col justify-end" data-id="element-978">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose} data-id="element-979" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="relative bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[72%] overflow-hidden" data-id="element-980">
            <div className="flex-shrink-0 pt-3 pb-1 flex justify-center" data-id="element-981">
              <div className="w-10 h-1.5 rounded-full bg-slate-200" data-id="element-982" />
            </div>
            <div className="flex-shrink-0 px-5 pt-2 pb-3 flex items-center justify-between border-b border-slate-100" data-id="element-983">
              <h3 className="text-base font-bold text-dark-navy" data-id="element-984">{title}</h3>
              <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 active:scale-95 transition-transform" data-id="element-985">
                <XIcon className="w-4 h-4" data-id="element-986" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3" data-id="element-987">
              <div className="flex flex-col gap-1" data-id="element-988">
                {options.map(o => {
              const isActive = o === active;
              return <button key={o} onClick={() => onSelect(o)} className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl text-left transition-colors ${isActive ? 'bg-indigo-50 text-primary-indigo font-bold' : 'active:bg-slate-50 text-slate-700 font-semibold'}`} data-id="element-989">
                      {o}
                      {isActive && <CheckIcon className="w-5 h-5 text-primary-indigo" data-id="element-990" />}
                    </button>;
            })}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, mountNode);
}